import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';

type Tab = 'rules' | 'history' | 'create';

const alertsApi = {
  listRules: () => api.get<any>('/alert-rules'),
  createRule: (data: any) => api.post<any>('/alert-rules', data),
  updateRule: (id: string, data: any) => api.put<any>(`/alert-rules/${id}`, data),
  deleteRule: (id: string) => api.post<any>(`/alert-rules/${id}`, { _method: 'DELETE' }),
  listHistory: () => api.get<any>('/alerts'),
  checkNow: () => api.post<any>('/alerts/check', {}),
  dismiss: (id: string) => api.post<any>(`/alerts/${id}/dismiss`, {}),
};

export default function Alerts() {
  const [activeTab, setActiveTab] = useState<Tab>('rules');
  const [rules, setRules] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, histRes] = await Promise.all([
        alertsApi.listRules().catch(() => ({ items: [] })),
        alertsApi.listHistory().catch(() => ({ items: [] })),
      ]);
      setRules(rulesRes.items || []);
      setHistory(histRes.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCheckNow = async () => {
    setChecking(true);
    setError('');
    try {
      const res = await alertsApi.checkNow();
      setMessage(`Alert check complete: ${res.alerts_triggered} alerts triggered across ${res.rules_checked} rules.`);
      loadData();
    } catch (err: any) { setError(err.message); }
    setChecking(false);
  };

  const handleToggleRule = async (id: string, isActive: boolean) => {
    try {
      await alertsApi.updateRule(id, { is_active: !isActive });
      setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: !isActive } : r));
    } catch (err: any) { setError(err.message); }
  };

  const handleDeleteRule = async (id: string, name: string) => {
    if (!window.confirm(`Delete alert rule "${name}"?`)) return;
    try {
      await alertsApi.deleteRule(id);
      setMessage('Rule deleted');
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (err: any) { setError(err.message); }
  };

  const handleDismiss = async (id: string) => {
    try {
      await alertsApi.dismiss(id);
      setHistory(prev => prev.map(h => h.id === id ? { ...h, status: 'dismissed' } : h));
    } catch (err: any) { setError(err.message); }
  };

  const handleCreateRule = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const split = (val: string) => val.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await alertsApi.createRule({
        name: form.get('name'),
        description: form.get('description'),
        keywords: split(form.get('keywords') as string || ''),
        naics_codes: split(form.get('naics_codes') as string || ''),
        agencies: split(form.get('agencies') as string || ''),
        set_asides: split(form.get('set_asides') as string || ''),
        notice_types: split(form.get('notice_types') as string || ''),
        psc_codes: split(form.get('psc_codes') as string || ''),
        notify_email: form.get('notify_email'),
        notify_frequency: form.get('notify_frequency'),
        notify_on_new: form.get('notify_on_new') === 'on',
        notify_on_deadline: form.get('notify_on_deadline') === 'on',
        deadline_days_before: parseInt(form.get('deadline_days_before') as string || '7'),
      });
      setMessage('Alert rule created');
      setActiveTab('rules');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const formatDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleString() : '—';

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };
  const btnPrimary: React.CSSProperties = { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
  const btnSmall: React.CSSProperties = { padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer', color: '#374151' };

  const activeAlerts = history.filter(h => h.status === 'active' || h.status === 'sent');

  const tabs: { id: Tab; label: string; icon: string; badge?: number }[] = [
    { id: 'rules', label: 'Alert Rules', icon: '⚙️' },
    { id: 'history', label: 'Notifications', icon: '🔔', badge: activeAlerts.length },
    { id: 'create', label: 'New Rule', icon: '➕' },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, margin: 0 }}>Alerts & Notifications</h2>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Automated monitoring for industry days and procurement notices</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleCheckNow} disabled={checking} style={{ ...btnPrimary, background: checking ? '#93c5fd' : '#7c3aed' }}>
            {checking ? '⏳ Checking...' : '🔍 Check Alerts Now'}
          </button>
          <button onClick={() => setActiveTab('create')} style={btnPrimary}>➕ New Rule</button>
        </div>
      </div>

      {message && <div role="status" aria-live="polite" style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#166534' }}>{message}<button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Dismiss message">x</button></div>}
      {error && <div role="alert" style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Dismiss error">x</button></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e5e7eb' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '12px 16px', background: activeTab === tab.id ? 'white' : 'transparent', color: activeTab === tab.id ? '#1f2937' : '#6b7280', border: 'none', borderBottom: activeTab === tab.id ? '2px solid #2563eb' : 'none', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500, cursor: 'pointer', position: 'relative' }}>
            {tab.icon} {tab.label}
            {tab.badge ? <span style={{ marginLeft: 6, background: '#dc2626', color: 'white', fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>{tab.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ─── RULES TAB ────────────────────────────────────────────── */}
      {activeTab === 'rules' && (
        <div>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
          ) : rules.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔔</div>
              <p style={{ color: '#6b7280', fontSize: 14 }}>No alert rules configured yet.</p>
              <button onClick={() => setActiveTab('create')} style={{ ...btnPrimary, marginTop: 12 }}>Create Your First Alert Rule</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rules.map((rule: any) => (
                <div key={rule.id} style={{ background: 'white', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb', opacity: rule.is_active ? 1 : 0.6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{rule.name}</span>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: rule.is_active ? '#dcfce7' : '#f3f4f6', color: rule.is_active ? '#166534' : '#6b7280', fontWeight: 600 }}>
                          {rule.is_active ? 'Active' : 'Paused'}
                        </span>
                      </div>
                      {rule.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{rule.description}</div>}
                      
                      {/* Criteria tags */}
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(rule.keywords || []).map((k: string, i: number) => (
                          <span key={`k${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: 3 }}>keyword: {k}</span>
                        ))}
                        {(rule.naics_codes || []).map((n: string, i: number) => (
                          <span key={`n${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 3 }}>NAICS: {n}</span>
                        ))}
                        {(rule.agencies || []).map((a: string, i: number) => (
                          <span key={`a${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#e0e7ff', color: '#3730a3', borderRadius: 3 }}>agency: {a}</span>
                        ))}
                        {(rule.set_asides || []).map((s: string, i: number) => (
                          <span key={`s${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#dcfce7', color: '#166534', borderRadius: 3 }}>set-aside: {s}</span>
                        ))}
                        {(rule.notice_types || []).map((t: string, i: number) => (
                          <span key={`t${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#fae8ff', color: '#86198f', borderRadius: 3 }}>type: {t}</span>
                        ))}
                        {(rule.psc_codes || []).map((p: string, i: number) => (
                          <span key={`p${i}`} style={{ fontSize: 10, padding: '2px 6px', background: '#fff7ed', color: '#c2410c', borderRadius: 3 }}>PSC: {p}</span>
                        ))}
                      </div>
                      
                      <div style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
                        Created: {formatDate(rule.created_at)} | Notify: {rule.notify_email || 'Not set'} | Frequency: {rule.notify_frequency || 'immediate'} | Triggered: {rule.trigger_count || 0} times
                        {rule.last_triggered > 0 && <span> | Last: {formatDate(rule.last_triggered)}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => handleToggleRule(rule.id, rule.is_active)} style={btnSmall}>
                        {rule.is_active ? '⏸️ Pause' : '▶️ Enable'}
                      </button>
                      <button onClick={() => handleDeleteRule(rule.id, rule.name)} aria-label="Delete rule" style={{ ...btnSmall, color: '#dc2626' }}>🗑️</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── HISTORY TAB ──────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
              <p style={{ color: '#6b7280', fontSize: 14 }}>No alerts triggered yet. Create rules and run a check.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((alert: any) => {
                const typeColors: Record<string, string> = { new_opportunity: '#2563eb', deadline_approaching: '#dc2626', industry_day: '#7c3aed' };
                const typeColor = typeColors[alert.match_type] || '#6b7280';
                const isDismissed = alert.status === 'dismissed';
                return (
                  <div key={alert.id} style={{ background: 'white', borderRadius: 8, padding: '12px 16px', border: '1px solid #e5e7eb', borderLeft: `4px solid ${typeColor}`, opacity: isDismissed ? 0.5 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, padding: '2px 8px', background: `${typeColor}15`, color: typeColor, borderRadius: 3, fontWeight: 600, textTransform: 'capitalize' }}>
                            {(alert.match_type || '').replace(/_/g, ' ')}
                          </span>
                          <span style={{ fontSize: 11, color: '#374151', fontWeight: 500 }}>{formatDate(alert.triggered_at)}</span>
                          {alert.status === 'sent' && <span style={{ fontSize: 10, color: '#10b981' }}>✓ Email sent</span>}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', marginTop: 4 }}>{alert.title}</div>
                        {alert.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{alert.description}</div>}
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Rule: {alert.rule_name}</div>
                      </div>
                      {!isDismissed && (
                        <button onClick={() => handleDismiss(alert.id)} style={btnSmall}>Dismiss</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── CREATE RULE TAB ──────────────────────────────────────── */}
      {activeTab === 'create' && (
        <div style={{ background: 'white', borderRadius: 8, padding: 24, border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 16, marginBottom: 4, color: '#1f2937' }}>Create Alert Rule</h3>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>Configure what you want to monitor. Alerts fire when new opportunities or events match your criteria.</p>
          
          <form onSubmit={handleCreateRule} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Rule Name</label>
              <input name="name" style={inputStyle} placeholder="e.g. Cybersecurity Opportunities - DoD" required />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Description</label>
              <input name="description" style={inputStyle} placeholder="Brief description of what this rule monitors" />
            </div>

            <div style={{ gridColumn: 'span 2', borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Matching Criteria</div>
            </div>
            
            <div>
              <label style={labelStyle}>Keywords (comma-separated)</label>
              <input name="keywords" style={inputStyle} placeholder="cybersecurity, zero trust, cloud" />
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Matches title and description</div>
            </div>
            <div>
              <label style={labelStyle}>NAICS Codes (comma-separated)</label>
              <input name="naics_codes" style={inputStyle} placeholder="541512, 541519, 518210" />
            </div>
            <div>
              <label style={labelStyle}>Agencies (comma-separated)</label>
              <input name="agencies" style={inputStyle} placeholder="Department of Defense, DHS, VA" />
            </div>
            <div>
              <label style={labelStyle}>Set-Asides (comma-separated)</label>
              <input name="set_asides" style={inputStyle} placeholder="8(a), small business, HUBZone, SDVOSB" />
            </div>
            <div>
              <label style={labelStyle}>Notice Types (comma-separated)</label>
              <input name="notice_types" style={inputStyle} placeholder="solicitation, presolicitation, industry_day" />
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>solicitation, presolicitation, industry_day, sources_sought</div>
            </div>
            <div>
              <label style={labelStyle}>PSC Codes (comma-separated)</label>
              <input name="psc_codes" style={inputStyle} placeholder="D301, D399, 7030" />
            </div>

            <div style={{ gridColumn: 'span 2', borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Notification Settings</div>
            </div>

            <div>
              <label style={labelStyle}>Notification Email</label>
              <input name="notify_email" type="email" style={inputStyle} placeholder="jordan@firstoinvest.com" />
            </div>
            <div>
              <label style={labelStyle}>Frequency</label>
              <select name="notify_frequency" style={inputStyle}>
                <option value="immediate">Immediate</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Summary</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Days Before Deadline to Alert</label>
              <input name="deadline_days_before" type="number" defaultValue={7} min={1} max={30} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" name="notify_on_new" defaultChecked /> Alert on new matching opportunities
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" name="notify_on_deadline" defaultChecked /> Alert when deadlines approach
              </label>
            </div>

            <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="submit" style={btnPrimary}>Create Alert Rule</button>
              <button type="button" onClick={() => setActiveTab('rules')} style={{ ...btnPrimary, background: '#6b7280' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
